import { authActions } from "../../store/auth-slice";
import { adminActions } from "../adminSlice";
import {
  getAccountDetails,
  getOnboardingUserStepsProgress,
} from "../../services/index";
import { redirectToShopifyInstall } from "../../utils/embeddedAppUtils";
import {
  isAuthRedirectDisabled,
  logSkippedAuthRedirect,
} from "../../utils/authRedirectUtils";
import { getWizardOnboardingRedirect } from "../../utils/onboardingStepsUtils";

/**
 * Legacy username/password login kept for the leftover /auth route.
 * Embedded boot uses authenticateEmbeddedSessionAction instead.
 */
export const authenticateUserAction = (params) => {
  return async (dispatch) => {
    dispatch(
      authActions.authenticateUser({
        data: [],
        shop: params?.username || null,
      })
    );
    return () => {};
  };
};

/**
 * Boots the embedded app for the current Shopify Admin session.
 *
 * There is no more login form or username/password exchange -- the App Bridge
 * session token (attached automatically by axiosInstance) *is* the credential.
 * We simply call /api/account/details; if the backend accepts the token we're
 * authenticated, otherwise we're not.
 *
 * On success, also fetches the shop's onboarding status and routes the merchant
 * to wherever they left off.
 *
 * `/api/account/details` responds with success:false whenever the session token's
 * shop has no install record at all -- either a brand-new shop that has never
 * been through OAuth, or one that uninstalled and is reinstalling. Since
 * `application_url` now points straight at this frontend (not the backend's old
 * /install entry point), nothing else kicks off OAuth for that shop, so we do it
 * here: top-level-redirect to /install, breaking out of the Shopify Admin iframe.
 */
export const authenticateEmbeddedSessionAction = (history) => {
  return async (dispatch) => {
    try {
      const res = await getAccountDetails();

      if (res?.success) {
        if (res.data?.installation_status === false) {
          dispatch(authActions.authenticateUser({ data: [], shop: null }));
          redirectToShopifyInstall();
          return;
        }
        const shop = res.data?.shop_name || null;
        dispatch(authActions.authenticateUser({ data: res.data, shop }));
        dispatch(
          adminActions.setAdminContext({
            isAdmin: res.data?.isAdmin,
            adminRole: res.data?.adminRole,
          })
        );

        try {
          const onboardingRes = await getOnboardingUserStepsProgress();
          const onboardingStatus = onboardingRes?.data ?? "";
          dispatch(authActions.setOnboardingStepsStatus(onboardingStatus));
          const redirect = getWizardOnboardingRedirect(onboardingStatus);
          if (redirect) {
            history.replace(redirect);
          }
        } catch (onboardingErr) {
          console.error("Error fetching onboarding status:", onboardingErr);
          // Unblock PrivateRoute cover-spin. Empty string keeps the admin nav hidden.
          dispatch(authActions.setOnboardingStepsStatus(""));
        }
      } else {
        dispatch(authActions.authenticateUser({ data: [], shop: null }));
        redirectToShopifyInstall();
      }
    } catch (err) {
      // 401 + shopify_reauth_required is handled by axiosInstance; also send
      // other boot failures through /install so a reinstall with a revoked
      // token cannot leave the merchant stuck on a logged-out embedded shell.
      console.error("Error authenticating embedded session:", err);
      dispatch(authActions.authenticateUser({ data: [], shop: null }));
      redirectToShopifyInstall();
    }
  };
};

//This method checks if a user logging in has completed onboarding and, if not, redirects them to the page where they left off.
export const checkOnboardingUserStepsAction = (params) => {
  return async (dispatch) => {
    getOnboardingUserStepsProgress()
      .then((res) => {
        const onboardingStatus = res.data;
        dispatch(authActions.setOnboardingStepsStatus(onboardingStatus ?? ""));
        const redirect = getWizardOnboardingRedirect(onboardingStatus);

        if (redirect) {
          if (isAuthRedirectDisabled()) {
            logSkippedAuthRedirect("onboarding steps redirect", redirect);
            return;
          }
          window.location.href = redirect;
        }
      })
      .catch((err) => {
        console.log(err);
      });

    return () => {};
  };
};
